import { create } from 'zustand'

interface QueryState<T> {
  data: T | null
  loading: boolean
  error: Error | null
}

interface QueryActions<T, P> {
  query: (params: P, callbacks?: QueryCallbacks<T>) => Promise<void>
  reset: () => void
}

export interface QueryCallbacks<T> {
  onSuccess?: (data: T) => void
  onError?: (error: Error) => void
}

export function createQueryStore<T, P = void>(
  fetchFn: (params: P) => Promise<T>,
  initialData: T | null = null,
) {
  return create<QueryState<T> & QueryActions<T, P>>(set => ({
    data: initialData,
    loading: false,
    error: null,
    query: async (params, { onSuccess, onError } = {}) => {
      set({ loading: true, error: null })
      try {
        const data = await fetchFn(params)
        set({ data, loading: false })
        onSuccess?.(data)
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        set({ error: err, loading: false })
        onError?.(err)
      }
    },
    reset: () => set({ data: initialData, loading: false, error: null }),
  }))
}
